import { stringifyPublisher } from '@provider/app-context';
import { resets } from '@ui/css';
import { formatTimestamp } from '@utils/common';
import { PDF_RICHTEXT_COLUMNIZER_SCRIPT, PDF_TEXT_COLUMNIZER_SCRIPT } from '../constants/constants/pdfColumnizerScript.js';
import { PDF_RICHTEXT_STYLESHEET, PDF_STYLESHEET } from '../constants/constants/pdfStyleSheet.js';

async function generatePdfExportConfig(component, config, fetchGlossaryWord) {
  return {
    format: "pdf",
    preset: getPresetByTemplate(config),
    postProcessDelay: 2,
    input: await generatePdfInput(component, config, fetchGlossaryWord),
    baseUrl: window.location.href,
    footerTemplate: generateFooterContent(config)
  };
}
async function generatePdfInput(component, config, fetchGlossaryWord) {
  const containerSelector = "[data-text-component-content]";
  const useOuterHtml = config.isRichText;
  const htmlContent = config.rawHtml ? component.html : getContentHtml(component.html, containerSelector, useOuterHtml);
  const glossaryWordContent = await extractGlossaryWordDefinitions(config.template, htmlContent, fetchGlossaryWord);
  const lineCountInterval = !config.isRichText && config.enableLineCount ? 5 : 0;
  const columnCount = !config.isRichText ? getColumnCountByTemplate(config.template) : 1;
  const richTextStylesheet = config.isRichText && component.ref ? PDF_RICHTEXT_STYLESHEET.replace("[FONT]", window.getComputedStyle(component.ref).fontFamily) : "";
  return `
    <html class="gPortal gPortal__content-wrapper pdf-print ${config.isRichText ? "richtext" : "text"}">
      <head>
        ${getCurrentStylesheets()}

        <style type="text/css">
          ${PDF_STYLESHEET}
          ${richTextStylesheet}
          ${config.customStyles ?? ""}
        </style>
      </head>

      <body class="gPortal-content gPortal-setting-theme-bright">
        ${config.title ? `
              <header>
                <h1>${config.title}</h1>
              </header>
            ` : ""}

        <div data-article-root class="${resets.rootCls}">
          <article
            class="gPortal-page"
            data-column-count="${columnCount}"
            data-line-interval="${lineCountInterval}"
          >
            ${htmlContent}
          </article>
        </div>

        ${glossaryWordContent ? `
              <footer style="column-count: ${getFooterColumnCountByTemplate(config.template)};">
                ${glossaryWordContent}
              </footer>
            ` : ""}

        <script type="text/javascript">
          ${config.isRichText ? PDF_RICHTEXT_COLUMNIZER_SCRIPT : PDF_TEXT_COLUMNIZER_SCRIPT}
        </script>

        <style>
          span {
            position: relative;
          }
        </style>
      </body>
    </html>
  `;
}
function getColumnCountByTemplate(template) {
  switch (template) {
    case "article":
    case "hans-reitzel":
    case "munksgaard":
      return 3;
    case "book":
    case "book-language":
      return 2;
    default:
      return 1;
  }
}
function getFooterColumnCountByTemplate(template) {
  switch (template) {
    case "source":
      return 2;
    default:
      return 3;
  }
}
function getPresetByTemplate(config) {
  if (config.isRichText) {
    return "portrait.full";
  }
  switch (config.template) {
    case "source":
      return "portrait.full";
    default:
      return "landscape.full";
  }
}
function generateFooterContent({
  user,
  publisher,
  productTitle,
  pageTitle,
  authors
}) {
  const userName = user?.name ?? "";
  const instName = user?.institution?.name ?? "";
  const title = (() => {
    const publisherTitle = stringifyPublisher(publisher);
    const baseTitle = productTitle?.length ? `${productTitle.toUpperCase()}, ${publisherTitle}` : `${publisherTitle}`;
    if (authors?.length && pageTitle?.length) {
      return `Fra <i>${pageTitle}</i> af ${authors.join(", ")}, ${baseTitle}`;
    }
    if (authors?.length) {
      return `Af ${authors.join(", ")}, ${baseTitle}`;
    }
    if (pageTitle?.length) {
      return `Fra <i>${pageTitle}</i>, ${baseTitle}`;
    }
    return baseTitle;
  })();
  return `
    <style>
      #footer { padding: 0; }
    </style>

    <div style="width: 100%; box-sizing: border-box; padding: 0 65px; text-align: center; color: #666; font: 12px/1.85 arial;">
      ${title}<br />

      Print og kopiering af dette materiale er tilladt, forudsat at det sker i
      overensstemmelse med skolens aftale med Tekst & Node.<br />

      ${(() => {
    if (userName && instName) {
      return `Denne pdf er udskrevet af ${userName}, ${instName}, ${formatTimestamp()}.`;
    }
    if (userName) {
      return `Denne pdf er udskrevet af ${userName}, ${formatTimestamp()}.`;
    }
    if (instName) {
      return `Denne pdf er udskrevet af ${instName}, ${formatTimestamp()}.`;
    }
    return `Denne pdf er udskrevet d. ${formatTimestamp()}.`;
  })()}
      <br />
      &nbsp;
    </div>
  `;
}

/**
 * helper that'll extract all currently applied stylesheets, so that we can
 * also inject them into the output that's going to be sent to the export
 * service.
 */
function getCurrentStylesheets() {
  return Array.from(document.styleSheets).filter(it => !it.media.mediaText.includes("screen")).map(extractCssTextFromStylesheet).join("\n");
}
function extractCssTextFromStylesheet(stylesheet) {
  if (!stylesheet) {
    return "";
  }
  try {
    // ignore stylesheets where we cannot access cssRules, they will need to
    // be referenced externally
    if (!stylesheet.cssRules) {
      throw new Error("continue to inject stylesheet in catch block");
    }
    const cssText = Array.from(stylesheet.cssRules).map(cssRule => {
      if (cssRule.type !== cssRule.IMPORT_RULE) {
        return cssRule.cssText;
      }

      // import rules are a special case, where we'll need to attempt to
      // inject the referenced css text directly, if at all possible
      try {
        const importCssRule = cssRule;
        const importedCss = extractCssTextFromStylesheet(importCssRule.styleSheet);
        return importCssRule.media.length > 0 ? `@media ${importCssRule.media.toString()} { ${importedCss} }` : importedCss;
      } catch {
        // if we couldn't resolve the import, then we need to simply allow the
        // ExportService to attempt to resolve it by adding the rule as it was
        return cssRule.cssText;
      }
    }).join("\n");
    return `<style type="${stylesheet.type}">${cssText}</style>`;
  } catch (err) {
    console.log(err);
    // if an error occurs while trying to extract styles, that means that we
    // didn't have permission due to CORS issues - simply reference the
    // file in the generated stylesheets
    return stylesheet.href ? `<link rel="stylesheet" type="${stylesheet.type}" href="${stylesheet.href}" />` : "";
  }
}

/**
 * helper that'll extract the content from the DOM, so we can easily intercept
 * the actually applied HTML code.
 */
function getContentHtml(html, containerSelector, useOuterHtml) {
  // create a wrapper node which will allow us to extract the content from the
  // component
  const componentWrapper = document.createElement("div");
  componentWrapper.innerHTML = html;

  // extract reference to the textual content by navigating up to the component
  // wrapper node, and then finding the ".gText__expanded" container inside
  const contentWrapper = componentWrapper.querySelector(containerSelector);
  const contentHtml = contentWrapper ? useOuterHtml ? contentWrapper.outerHTML : contentWrapper.innerHTML : "";
  return numerateGlossaryWords(contentHtml);
}
function numerateGlossaryWords(htmlContent) {
  // create an in-memory element fragment which we're going to render the
  // content inside, so that we can use querySelector to quickly identify all of
  // the glossary words that are embedded in the content and insert numerations
  //
  // (note that the actual numerations are inserted inside CSS using counters,
  // refer to pdfStyleSheet.ts)
  const fragment = document.createElement("div");
  fragment.innerHTML = htmlContent;
  fragment.querySelectorAll(".gGlossaryWord__word").forEach(word => {
    if (word.textContent?.trim()) {
      word.appendChild(document.createElement("sup"));
    }
  });
  return fragment.innerHTML;
}

/**
 * helper that'll iterate through the content of a PDF and extract every
 * single glossary word and it's definition so that it can be rendered in the
 * footer of the document.
 */
async function extractGlossaryWordDefinitions(template, htmlContent, fetchGlossaryWord) {
  // create an in-memory element fragment which we're going to render the
  // content inside, so that we can use querySelector to quickly identify all of
  // the glossary words that are embedded in the content
  const fragment = document.createElement("div");
  fragment.innerHTML = htmlContent;

  // scan through all glossary words and load their definitions in parallel
  const glossaryWords = await Promise.all(Array.from(fragment.querySelectorAll(".gGlossaryWord__word")).filter(wordElement => wordElement.textContent?.trim()).map(async wordElement => {
    const wordId = wordElement.getAttribute("data-word-id");
    const wordContent = wordElement.textContent ?? "";
    if (!wordId) {
      // give up if word id couldn't be identified, as we'll not be able to
      // fetch definition in that case anyway
      return {
        word: wordContent,
        definition: ""
      };
    }
    return {
      word: wordElement.textContent ?? "",
      definition: await fetchGlossaryWord(wordId)
    };
  }));
  if (!glossaryWords.length) {
    // give up if no glossary words were identified
    return undefined;
  }
  return glossaryWords.map((it, index) => {
    const word = template !== "book-language" ? `${it.word}:` : "";
    return `
        <div class="gGlossaryWord__pdfDefinition">
          <b><sup>${index + 1}</sup>${word}</b>
          ${it.definition}
        </div>
      `;
  }).join("\n");
}

export { generatePdfExportConfig };
//# sourceMappingURL=generatePdfExportConfig.js.map
